/** Puts+ / Calls+ cutoff controls for SF+ Settings (shared with the tab filters). */

import { isAgenticFeatureEnabled } from "./agenticFeatures";
import {
  fetchTradierWheelSettings,
  saveTradierWheelSettings,
  TRADIER_WHEEL_DEFAULTS,
  TRADIER_WHEEL_SETTINGS_CHANGED_EVENT,
  type TradierWheelSettings,
} from "./tradierWheelPanel";

export { TRADIER_WHEEL_DEFAULTS, TRADIER_WHEEL_SETTINGS_CHANGED_EVENT };

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function clampSettings(raw: Partial<TradierWheelSettings>): TradierWheelSettings {
  const minPop = Number(raw.minPop);
  const maxDtePuts = Number(raw.maxDtePuts);
  const maxDteCalls = Number(raw.maxDteCalls);
  return {
    minPop: Number.isFinite(minPop)
      ? Math.max(0, Math.min(100, Math.round(minPop)))
      : TRADIER_WHEEL_DEFAULTS.minPop,
    maxDtePuts: Number.isFinite(maxDtePuts)
      ? Math.max(1, Math.min(180, Math.round(maxDtePuts)))
      : TRADIER_WHEEL_DEFAULTS.maxDtePuts,
    maxDteCalls: Number.isFinite(maxDteCalls)
      ? Math.max(1, Math.min(180, Math.round(maxDteCalls)))
      : TRADIER_WHEEL_DEFAULTS.maxDteCalls,
  };
}

export function notifyTradierWheelSettingsChanged(settings: TradierWheelSettings): void {
  window.dispatchEvent(
    new CustomEvent(TRADIER_WHEEL_SETTINGS_CHANGED_EVENT, { detail: { settings } }),
  );
}

export async function loadTradierWheelSettingsContext(): Promise<{
  settings: TradierWheelSettings;
  configured: boolean;
} | null> {
  if (!isAgenticFeatureEnabled()) return null;
  try {
    const res = await fetchTradierWheelSettings();
    return {
      settings: clampSettings(res.settings ?? TRADIER_WHEEL_DEFAULTS),
      configured: Boolean(res.configured),
    };
  } catch {
    return {
      settings: { ...TRADIER_WHEEL_DEFAULTS },
      configured: false,
    };
  }
}

function fieldsHtml(settings: TradierWheelSettings, variant: "web" | "mobile"): string {
  const fieldClass = variant === "mobile" ? "sfe-mobile-settings-field" : "sfe-modal-field sfe-web-settings-field";
  return `
    <div class="sfe-tradier-settings-fields">
      <label class="${fieldClass}">
        <span>Min PoP %</span>
        <input type="number" min="0" max="100" step="1" data-tradier-settings-min-pop value="${esc(String(settings.minPop))}" />
      </label>
      <label class="${fieldClass}">
        <span>Max DTE (Puts+)</span>
        <input type="number" min="1" max="180" step="1" data-tradier-settings-max-dte-puts value="${esc(String(settings.maxDtePuts))}" />
      </label>
      <label class="${fieldClass}">
        <span>Max DTE (Calls+)</span>
        <input type="number" min="1" max="180" step="1" data-tradier-settings-max-dte-calls value="${esc(String(settings.maxDteCalls))}" />
      </label>
    </div>
    <p class="muted small sfe-tradier-settings-status" data-tradier-settings-status hidden></p>`;
}

export function tradierWheelSettingsWebHtml(
  settings: TradierWheelSettings,
  opts: { configured?: boolean } = {},
): string {
  const configuredNote = opts.configured === false
    ? `<p class="muted small sfe-column-settings-lead">Tradier token is not configured on the server — boards stay empty until it is.</p>`
    : "";
  return `
    <section class="sfe-web-settings-card sfe-web-settings-card--full" data-sfe-tradier-settings-host>
      <h4 class="sfe-web-settings-card-title">Puts+ / Calls+</h4>
      <p class="muted small sfe-column-settings-lead">Tradier board cutoffs. Same Min PoP applies to both tabs; Max DTE can differ per side.</p>
      ${configuredNote}
      ${fieldsHtml(settings, "web")}
    </section>`;
}

export function tradierWheelSettingsMobileHtml(
  settings: TradierWheelSettings,
  collapsibleAttrs: string,
  opts: { configured?: boolean } = {},
): string {
  const configuredNote = opts.configured === false
    ? `<p class="sfe-mobile-status">Tradier token is not configured on the server — boards stay empty until it is.</p>`
    : "";
  return `
    <details class="wheel-settings-section sfe-mobile-settings-section sfe-mobile-settings-collapsible" data-sfe-collapsible="settings-tradier-wheel" data-sfe-collapsible-default="closed"${collapsibleAttrs}>
      <summary class="sfe-mobile-settings-section-summary">
        <span class="wheel-settings-section-title">Puts+ / Calls+</span>
      </summary>
      <div class="sfe-mobile-settings-section-body" data-sfe-tradier-settings-host>
        <p class="sfe-mobile-status">Tradier board cutoffs. Same Min PoP applies to both tabs; Max DTE can differ per side.</p>
        ${configuredNote}
        ${fieldsHtml(settings, "mobile")}
      </div>
    </details>`;
}

function readInputs(host: ParentNode): TradierWheelSettings {
  return clampSettings({
    minPop: Number(host.querySelector<HTMLInputElement>("[data-tradier-settings-min-pop]")?.value),
    maxDtePuts: Number(
      host.querySelector<HTMLInputElement>("[data-tradier-settings-max-dte-puts]")?.value,
    ),
    maxDteCalls: Number(
      host.querySelector<HTMLInputElement>("[data-tradier-settings-max-dte-calls]")?.value,
    ),
  });
}

export function bindTradierWheelSettings(
  host: ParentNode,
  options: {
    onError?: (message: string) => void;
    onSaved?: (settings: TradierWheelSettings) => void;
  } = {},
): () => void {
  let saveTimer: ReturnType<typeof setTimeout> | null = null;
  let disposed = false;

  const setStatus = (message: string): void => {
    const el = host.querySelector<HTMLElement>("[data-tradier-settings-status]");
    if (!el) return;
    el.hidden = !message;
    el.textContent = message;
  };

  const persist = async (): Promise<void> => {
    const next = readInputs(host);
    try {
      const res = await saveTradierWheelSettings(next);
      if (disposed) return;
      const saved = clampSettings(res.settings ?? next);
      const minEl = host.querySelector<HTMLInputElement>("[data-tradier-settings-min-pop]");
      const putsEl = host.querySelector<HTMLInputElement>("[data-tradier-settings-max-dte-puts]");
      const callsEl = host.querySelector<HTMLInputElement>("[data-tradier-settings-max-dte-calls]");
      if (minEl) minEl.value = String(saved.minPop);
      if (putsEl) putsEl.value = String(saved.maxDtePuts);
      if (callsEl) callsEl.value = String(saved.maxDteCalls);
      setStatus("Saved");
      notifyTradierWheelSettingsChanged(saved);
      options.onSaved?.(saved);
      window.setTimeout(() => {
        if (!disposed) setStatus("");
      }, 1500);
    } catch (error) {
      if (disposed) return;
      const message = error instanceof Error ? error.message : "Could not save Puts+/Calls+ settings";
      setStatus(message);
      options.onError?.(message);
    }
  };

  const scheduleSave = (): void => {
    if (saveTimer) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(() => {
      saveTimer = null;
      void persist();
    }, 350);
  };

  const onChange = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (
      !target.matches(
        "[data-tradier-settings-min-pop], [data-tradier-settings-max-dte-puts], [data-tradier-settings-max-dte-calls]",
      )
    ) {
      return;
    }
    scheduleSave();
  };

  host.addEventListener("change", onChange);
  host.addEventListener("input", onChange);

  return () => {
    disposed = true;
    if (saveTimer) window.clearTimeout(saveTimer);
    host.removeEventListener("change", onChange);
    host.removeEventListener("input", onChange);
  };
}
